import { useContext, useEffect, useRef, useState } from "react";
import {
  getHouseholdSummaryParts,
  haveValue,
  toast,
} from "../../helper/common";
import ApplicationContext from "../../context/application/ApplicationContext";
import useTranslator from "../../hooks/useTranslator";
import AuthContext from "../../context/auth/AuthContext";
import { Tooltip as ReactTooltip } from "react-tooltip";

import {
  resolveParticipantName,
  getApplicationTitle,
  sortParticipantsWithGuarantors,
  toIdString,
  canJoinAsGuarantor,
} from "../../helper/applicationHelpers";
import SkorProtectBadge from "../SkorProtectBadge/SkorProtectBadge";
import TenantRequirementsFlow from "../TenantRequirements/TenantRequirementsFlow";
import ConsentStepModal from "../../pages/onboarding/ConsentStepModal";
import { getPendingSkorProtectActions } from "../../services/sharedService";
import constants from "../../Utils/constants";
import {
  RowButton,
  Avatar,
  Badge,
  Button,
  ChoiceGroup,
  Drawer,
  Icon,
  InfoList,
  Menu,
  Modal,
  Select,
  SkeletonList,
} from "../design";
import LockedFeature from "./LockedFeature";
import useWorkspaceRequirements from "../../hooks/useWorkspaceRequirements";
import "./ApplicationsModal.css";

const resolveCreatorName = (created_by) => {
  if (!created_by || typeof created_by === "string") return "";
  const fname = created_by.fname || created_by.firstname || "";
  const lname = created_by.lname || created_by.lastname || "";
  return `${fname} ${lname}`.trim() || created_by.email || "";
};

const resolveCreatorEmail = (created_by) => {
  if (!created_by || typeof created_by === "string") return null;
  return created_by.email ?? null;
};

const resolveCreatorId = (created_by) => {
  if (!created_by) return null;
  if (typeof created_by === "string") return created_by;
  return created_by._id ?? null;
};

const formatPaymentTimeRemaining = (expiresAt, now) => {
  const remainingMs = Math.max(0, new Date(expiresAt).getTime() - now);
  const totalMinutes = Math.floor(remainingMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};
const getParticipantGuarantors = (participant) =>
  participant?.consent_status === "accepted" &&
  Array.isArray(participant?.guarantors)
    ? participant.guarantors
    : [];

/* ── role badge ── */
const ROLE_BADGES = {
  deleted: { tone: "neutral", key: "deletedUser", fallback: "Deleted user" },
  owner: { tone: "ink", key: "owner", fallback: "Owner" },
  invited: { tone: "warning", key: "invited", fallback: "Invited" },
  accepted: { tone: "lime", key: "accepted", fallback: "Accepted" },
  pending: { tone: "warning", key: "pending", fallback: "Pending" },
  rejected: { tone: "danger", key: "rejected", fallback: "Rejected" },
};

const RoleBadge = ({ role, _t }) => {
  const badge = ROLE_BADGES[role];
  if (!badge) return null;
  return <Badge tone={badge.tone}>{_t(badge.key, badge.fallback)}</Badge>;
};

const ClientIdentificationPendingBadge = ({ canSign, onSign, _t }) => {
  const badge = <Badge tone="warning">{_t("amlSigningPending")}</Badge>;
  if (canSign) {
    return (
      <RowButton
        type="button"
        className="applications-badge-button"
        onClick={(event) => {
          event.stopPropagation();
          onSign();
        }}
      >
        {badge}
      </RowButton>
    );
  }

  return badge;
};

const MoreIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <circle cx="12" cy="5" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="12" cy="19" r="1.6" />
  </svg>
);

/* three-dot menu on a participant row: change to guarantor / flatmate */
const ParticipantRoleMenu = ({
  participant,
  open,
  onToggle,
  onClose,
  onChange,
  busy,
  // they have guarantors, so they cannot become one
  guarantorBlocked = false,
  _t,
}) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const isGuarantor = participant.is_guarantor === true;
  return (
    <div
      className="applications-card__menu-anchor"
      ref={ref}
      onClick={(e) => e.stopPropagation()}
    >
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        aria-label="menu"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
        iconStart={<MoreIcon />}
        onClick={onToggle}
      />
      {open && (
        <Menu
          className="applications-card__menu"
          items={[
            {
              key: "role",
              label: isGuarantor
                ? _t("changeToFlatmate")
                : _t("changeToGuarantor"),
              icon: <Icon name={isGuarantor ? "users" : "shield"} />,
              disabled: !isGuarantor && guarantorBlocked,
              className:
                !isGuarantor && guarantorBlocked
                  ? "applications-roleMenu__blocked"
                  : "",
              onClick: () => {
                onClose();
                onChange(isGuarantor ? "flatmate" : "guarantor");
              },
            },
          ]}
        />
      )}
      {open && !isGuarantor && guarantorBlocked && (
        <ReactTooltip
          anchorSelect=".applications-roleMenu__blocked"
          place="left"
          className="react-tooltip-styling"
          // the shared tooltip style colours text inside a <p> only
          render={() => (
            <p>
              {_t(
                "guarantorCannotHaveGuarantor",
                "There cannot be a guarantor of a guarantor."
              )}
            </p>
          )}
        />
      )}
    </div>
  );
};

/* ── single application card ── */
const ApplicationCard = ({
  app,
  currentUserEmail,
  currentUserId,
  currentUserClientIdentificationStatus,
  currentUserDeclarationConsent,
  onAccept,
  onReject,
  onDelete,
  processingId,
  openMenuId,
  setOpenMenuId,
  onCloseModal,
  llShowOnly,
  onSignClientIdentification,
  onSignAffidavit,
  pendingSkorProtectAction,
  now,
  onPaySkorProtect,
  paymentProcessingId,
  onViewPaymentReceipt,
}) => {
  const { _t } = useTranslator();
  const { changeParticipantRole } = useContext(ApplicationContext);
  // Household and pets are locked when the workspace does not ask applicants
  const { isEnabled, FEATURES } = useWorkspaceRequirements();
  const [expanded, setExpanded] = useState(false);
  const menuRef = useRef(null);
  // email of the participant whose role menu is open / being saved
  const [roleMenuEmail, setRoleMenuEmail] = useState(null);
  const [roleSavingEmail, setRoleSavingEmail] = useState(null);
  // { participant, role } waiting for the person to confirm
  const [roleConfirm, setRoleConfirm] = useState(null);
  // Accept asks how to join ("flatmate" | "guarantor") when they can be a
  // guarantor in this application; null while the choice is closed
  const [joinAs, setJoinAs] = useState(null);
  const isProcessing = processingId === app._id;
  const menuOpen = openMenuId === app._id;
  const skorProtectActionStatus =
    pendingSkorProtectAction?.status ?? "waiting_for_payment";
  const isSkorProtectPaymentPending =
    skorProtectActionStatus === "waiting_for_payment";
  const isSkorProtectPaymentReceived =
    skorProtectActionStatus === "waiting_for_rental_contract";
  const isSkorProtectActive = skorProtectActionStatus === "active";
  const showSkorProtectDocuments =
    isSkorProtectPaymentReceived || isSkorProtectActive;
  const activePendingSkorProtectAction =
    pendingSkorProtectAction &&
    (showSkorProtectDocuments ||
      (isSkorProtectPaymentPending &&
        new Date(pendingSkorProtectAction.expires_at).getTime() > now))
      ? pendingSkorProtectAction
      : null;
  const isPaymentProcessing =
    paymentProcessingId === activePendingSkorProtectAction?.skor_protect_id;

  const participants = sortParticipantsWithGuarantors(app.participants ?? []);
  const creatorEmail = resolveCreatorEmail(app.created_by);
  const creatorId = resolveCreatorId(app.created_by);
  const iAmOwner =
    (creatorId && creatorId === currentUserId) ||
    (creatorEmail && creatorEmail.toLowerCase() === currentUserEmail);

  const isPendingInvitation =
    app.isPendingInvitation ??
    (!iAmOwner &&
      participants.some(
        (p) =>
          p.email?.toLowerCase() === currentUserEmail &&
          p.consent_status === "pending"
      ));

  const pendingCount = participants.filter(
    (p) =>
      p.consent_status === "pending" &&
      p.email?.toLowerCase() !== creatorEmail?.toLowerCase()
  ).length;

  const shareRequests = app.shareRequests ?? [];
  const sharedProfiles = (app.sharedProfiles ?? []).filter(
    (sharedProfile) =>
      sharedProfile?.id?._id || sharedProfile?.shared_by_deleted
  );
  const totalShared = shareRequests.length + sharedProfiles.length;
  const householdParts = getHouseholdSummaryParts(app.household_pets, _t);
  const clientIdentificationRequired =
    app.client_identification_required === true;
  const currentParticipant = participants.find(
    (participant) =>
      participant?.id?.toString() === currentUserId?.toString() ||
      participant?.email?.toLowerCase() === currentUserEmail?.toLowerCase()
  );
  const currentUserClientIdentificationPending =
    clientIdentificationRequired &&
    currentParticipant?.consent_status === constants.ACCEPTED &&
    (currentParticipant?.client_identification_consent_status ??
      currentUserClientIdentificationStatus) !== constants.ACCEPTED;
  const currentUserAffidavitPending =
    app.affidavit_required === true &&
    currentParticipant?.consent_status === constants.ACCEPTED &&
    !haveValue(currentUserDeclarationConsent);
  const showSkorProtectStatus = !currentUserClientIdentificationPending;

  /* status badge under the card header */
  const getStatusBadge = () => {
    if (isPendingInvitation) {
      const name = resolveCreatorName(app.created_by);
      return (
        <Badge tone="warning">
          {_t("invitationFrom")} {name}
        </Badge>
      );
    }

    if (pendingCount > 0) {
      return (
        <Badge tone="warning">
          {_t("pendingConsent")} &bull; {pendingCount}{" "}
          {pendingCount === 1 ? _t("flatmate") : _t("flatmates")}
        </Badge>
      );
    }

    if (totalShared > 0) {
      return (
        <Badge tone="lime">
          {_t("sharedWithApplication")} {totalShared}{" "}
          {totalShared === 1 ? _t("landlordApp") : _t("landlordsApp")}
        </Badge>
      );
    }

    return null;
  };

  /* determine the role of a single participant */
  const getRole = (participant) => {
    if (participant.is_deleted || participant.deleted) return "deleted";
    const isOwnerParticipant =
      creatorEmail &&
      participant.email?.toLowerCase() === creatorEmail.toLowerCase();
    if (isOwnerParticipant) return "owner";

    const isMe = participant.email?.toLowerCase() === currentUserEmail;
    if (isMe && participant.consent_status === "pending") return "invited";

    return participant.consent_status ?? "pending";
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  // Tenants a participant can become the guarantor of: active, non-guarantor
  // people with an account, other than themselves
  const getGuarantorCandidates = (participant) =>
    participants.filter(
      (other) =>
        !other?.is_deleted &&
        other?.is_guarantor !== true &&
        toIdString(other?.id) &&
        other?.email !== participant?.email
    );

  // Opens the confirmation; for "guarantor" it preselects who they back
  // (the one they already back, else the lead, else the first tenant)
  const openRoleConfirm = (participant, role) => {
    let guarantorOf = "";
    if (role === "guarantor") {
      const candidates = getGuarantorCandidates(participant);
      const current = candidates.find(
        (other) =>
          toIdString(other.id) === toIdString(participant?.guarantor_of)
      );
      const lead = candidates.find((other) => other.is_lead);
      guarantorOf = toIdString((current ?? lead ?? candidates[0])?.id);
    }
    setRoleConfirm({ participant, role, guarantorOf });
  };

  const handleRoleChange = async (participant, role, guarantorOf) => {
    try {
      setRoleSavingEmail(participant.email);
      await changeParticipantRole(
        app._id,
        participant.email,
        role,
        guarantorOf
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? _t("failedToChangeParticipantRole")
      );
    } finally {
      setRoleSavingEmail(null);
    }
  };

  const menuItems = [];
  if (showSkorProtectDocuments) {
    menuItems.push(
      {
        key: "receipt",
        label: _t("viewPaymentReceipt"),
        icon: <Icon name="fileText" />,
        onClick: () => {
          setOpenMenuId(null);
          onViewPaymentReceipt(activePendingSkorProtectAction);
        },
      },
      {
        key: "terms",
        label: _t("viewSkorProtectTermsAndConditions"),
        icon: <Icon name="shield" />,
        onClick: () => {
          setOpenMenuId(null);
          window.open(
            "https://www.skortorent.com/inquilino/skor-protect/",
            "_blank",
            "noopener,noreferrer"
          );
        },
      }
    );
  }
  menuItems.push(
    {
      key: "share",
      label: _t("shareApplicationWithLandlord", "Share with landlord"),
      icon: <Icon name="send" />,
      onClick: () => {
        setOpenMenuId(null);
        onCloseModal();
        const url = new URL(window.location.href);
        url.searchParams.set("shareprofilemodal", "true");
        url.searchParams.set("applicationId", app._id);
        window.history.replaceState(
          window.history.state,
          "",
          `${url.pathname}${url.search}${url.hash}`
        );
        window.dispatchEvent(new Event("open-share-profile-modal"));
      },
    },
    {
      key: "delete",
      danger: true,
      label: isProcessing
        ? _t("deleting", "Deleting...")
        : _t("deleteApplication", "Delete application"),
      icon: <Icon name="trash" />,
      onClick: () => {
        if (!isProcessing) {
          setOpenMenuId(null);
          onDelete(app._id);
        }
      },
    }
  );

  return (
    <div
      onClick={() => {
        setExpanded((v) => !v);
        setOpenMenuId(null);
      }}
      className="applications-card"
    >
      {/* ── header row ── */}
      <div className="applications-card__row">
        <div className="applications-card__info">
          <div className="applications-card__title-row">
            <p
              className="applications-card__name"
              title={getApplicationTitle(app, _t)}
            >
              {getApplicationTitle(app, _t)}
            </p>
            {participants.length > 0 && (
              <Badge tone="neutral">
                <Icon name="users" size={12} />
                {participants.length}
              </Badge>
            )}
            {app.skor_protect && <SkorProtectBadge />}
          </div>
          <div className="applications-card__chips">
            {currentUserClientIdentificationPending && (
              <Badge tone="warning">{_t("amlSigningPending")}</Badge>
            )}
            {currentUserAffidavitPending && (
              <Badge tone="warning">
                {_t("declarationSigningPending", "Declaration pending")}
              </Badge>
            )}
            {showSkorProtectStatus &&
              activePendingSkorProtectAction &&
              isSkorProtectPaymentPending && (
                <Badge tone="warning">
                  {_t("skorProtect")} · {_t("paymentPending")} ·{" "}
                  {formatPaymentTimeRemaining(
                    activePendingSkorProtectAction.expires_at,
                    now
                  )}
                </Badge>
              )}
            {showSkorProtectStatus &&
              activePendingSkorProtectAction &&
              isSkorProtectPaymentReceived && (
                <>
                  <span data-tooltip-id={`skor-protect-paid-${app._id}`}>
                    <Badge tone="lime">
                      <Icon name="shield" size={12} />
                      {_t("skorProtect")} · {_t("paid")}
                      <Icon name="info" size={12} />
                    </Badge>
                  </span>
                  <ReactTooltip
                    id={`skor-protect-paid-${app._id}`}
                    place="bottom"
                    className="react-tooltip-styling application-skor-protect-paid-tooltip"
                    render={() => (
                      <p>
                        {_t(
                          "skorProtectPaidTooltip",
                          "Payment received. Skor Protect will become active as soon as the landlord uploads the rental agreement."
                        )}
                      </p>
                    )}
                  />
                </>
              )}
            {showSkorProtectStatus &&
              activePendingSkorProtectAction &&
              isSkorProtectActive && (
                <Badge tone="lime">
                  <Icon name="shield" size={12} />
                  {_t("skorProtect")} · {_t("active")}
                </Badge>
              )}
          </div>
          <div className="applications-avatars">
            {participants.slice(0, 4).map((p, i) => {
              const name = resolveParticipantName(
                p,
                _t("deletedUser", "Deleted user")
              );
              const isPending = p.consent_status === "pending";
              return (
                <span
                  key={i}
                  className={`applications-avatars__item${isPending ? " is-pending" : ""}`}
                  title={name}
                >
                  <Avatar name={name} size="sm" />
                </span>
              );
            })}
            {participants.length > 4 && (
              <Badge tone="neutral">+{participants.length - 4}</Badge>
            )}
          </div>
        </div>

        <div className="applications-card__controls">
          {isPendingInvitation ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onReject(app._id);
                }}
                disabled={isProcessing}
              >
                {_t("decline", "Decline")}
              </Button>
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    canJoinAsGuarantor(app, {
                      _id: currentUserId,
                      email: currentUserEmail,
                    })
                  ) {
                    setJoinAs("flatmate");
                  } else {
                    onAccept(app._id);
                  }
                }}
                disabled={isProcessing}
              >
                {_t("accept", "Accept")}
              </Button>
            </>
          ) : !llShowOnly ? (
            <div
              className="applications-card__menu-anchor"
              ref={menuRef}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label="menu"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                iconStart={<MoreIcon />}
                onClick={() => setOpenMenuId(menuOpen ? null : app._id)}
              />
              {menuOpen && (
                <Menu className="applications-card__menu" items={menuItems} />
              )}
            </div>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="toggle"
            aria-expanded={expanded}
            iconStart={
              <Icon
                name="chevronDown"
                className={`applications-card__chevron${expanded ? " is-open" : ""}`}
              />
            }
          />
        </div>
      </div>

      {/* ── status badge + action ── */}
      {((!llShowOnly && getStatusBadge()) ||
        currentUserClientIdentificationPending ||
        currentUserAffidavitPending ||
        (activePendingSkorProtectAction && isSkorProtectPaymentPending)) && (
        <div className="applications-card__footer">
          {!llShowOnly && getStatusBadge()}
          {!llShowOnly && currentUserClientIdentificationPending ? (
            <Button
              size="sm"
              iconEnd={<Icon name="arrowRight" />}
              onClick={(event) => {
                event.stopPropagation();
                onSignClientIdentification(app);
              }}
            >
              {_t("viviaSign")}
            </Button>
          ) : !llShowOnly && currentUserAffidavitPending ? (
            <Button
              size="sm"
              iconEnd={<Icon name="arrowRight" />}
              onClick={(event) => {
                event.stopPropagation();
                onSignAffidavit();
              }}
            >
              {_t("viviaSign")}
            </Button>
          ) : activePendingSkorProtectAction && isSkorProtectPaymentPending ? (
            <Button
              size="sm"
              loading={isPaymentProcessing}
              iconEnd={<Icon name="arrowRight" />}
              onClick={async (event) => {
                event.stopPropagation();
                await onPaySkorProtect(activePendingSkorProtectAction);
              }}
            >
              {isPaymentProcessing ? _t("redirecting") : _t("paySkorProtect")}
            </Button>
          ) : null}
        </div>
      )}

      {/* ── expanded: tenants list ── */}
      {expanded && (
        <>
          {/* Household and pets: sent only when the landlord asks for it;
              null means the application was shared before it was asked */}
          {llShowOnly && app.household_pets !== undefined && (
            <LockedFeature locked={!isEnabled(FEATURES.APPLICANT_QUESTIONS)}>
              <div className="applications-card__section">
                <InfoList
                  layout="stacked"
                  items={[
                    {
                      label: _t("householdLabel"),
                      value: householdParts ? (
                        <>
                          {/* Parts the landlords did not ask are empty */}
                          {householdParts.residents && (
                            <strong>{householdParts.residents}</strong>
                          )}
                          {householdParts.residents &&
                            householdParts.breakdown &&
                            " "}
                          {householdParts.breakdown && (
                            <span>{householdParts.breakdown}</span>
                          )}
                          {(householdParts.residents ||
                            householdParts.breakdown) &&
                            householdParts.pets &&
                            " · "}
                          {householdParts.pets && (
                            <strong>{householdParts.pets}</strong>
                          )}
                          {householdParts.ppp && (
                            <span> {householdParts.ppp}</span>
                          )}
                        </>
                      ) : (
                        _t("householdNotProvided")
                      ),
                    },
                  ]}
                />
              </div>
            </LockedFeature>
          )}
          {/* LinkedIn and the workspace's own questions, answered by each
              tenant once for this workspace */}
          {llShowOnly && (app.applicant_answers ?? []).length > 0 && (
            <LockedFeature locked={!isEnabled(FEATURES.APPLICANT_QUESTIONS)}>
              <div className="application-household">
                <p className="application-household-title">
                  {_t("applicantQuestions")}
                </p>
                {app.applicant_answers.map((entry) => {
                  const owner = participants.find(
                    (p) => p.id?.toString() === entry.tenant_id?.toString()
                  );
                  const lines = [
                    ...(entry.linkedin
                      ? [
                          {
                            key: "linkedin",
                            label: "LinkedIn",
                            value: entry.linkedin,
                            isLink: true,
                          },
                        ]
                      : []),
                    ...(entry.answers ?? []).map((answer) => ({
                      key: answer.question_id,
                      label: answer.text,
                      value: answer.value,
                      isLink: answer.type === "link",
                    })),
                  ];
                  if (lines.length === 0) return null;
                  return (
                    <div key={entry.tenant_id}>
                      {participants.length > 1 && owner && (
                        <p className="application-household-line">
                          <strong>
                            {resolveParticipantName(
                              owner,
                              _t("deletedUser", "Deleted user")
                            )}
                          </strong>
                        </p>
                      )}
                      {lines.map((line) => (
                        <p
                          key={line.key}
                          className="application-household-line"
                        >
                          <span>{line.label}: </span>
                          {line.isLink ? (
                            <a
                              href={line.value}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {line.value}
                            </a>
                          ) : (
                            <strong>
                              {line.value === true
                                ? _t("yes")
                                : line.value === false
                                  ? _t("no")
                                  : Array.isArray(line.value)
                                    ? line.value.join(", ")
                                    : String(line.value)}
                            </strong>
                          )}
                        </p>
                      ))}
                    </div>
                  );
                })}
              </div>
            </LockedFeature>
          )}
          {participants.length > 0 && (
            <div className="applications-card__section">
              <p className="applications-card__label">
                {_t("tenantsInThisApplication", "TENANTS IN THIS APPLICATION")}
              </p>
              {participants.map((p, i) => {
                const role = getRole(p);
                const isDeleted = p.is_deleted === true;
                const isAccepted = p.consent_status === "accepted";
                const isMe =
                  !isDeleted && p.email?.toLowerCase() === currentUserEmail;
                const isOwnerRow = role === "owner";
                const participantId = p.id;
                const showClientIdentificationConsentStatus =
                  clientIdentificationRequired && isAccepted && !isDeleted;
                // deleted members have no email: never match the null "none"
                const isRoleSaving = !!p.email && roleSavingEmail === p.email;
                const isClientIdentificationConsentPending =
                  showClientIdentificationConsentStatus &&
                  p.client_identification_consent_status !== constants.ACCEPTED;
                const hasTenantId = participantId && llShowOnly && !isDeleted;
                const canNavigate = hasTenantId && !isMe;

                /* display name: always show "You" when it's the current user */
                const displayName = isMe
                  ? _t("you", "You")
                  : resolveParticipantName(
                      p,
                      _t("deletedUser", "Deleted user")
                    );

                /* subtitle: "Owner" label for owner row, otherwise email */
                const subtitle = isDeleted
                  ? ""
                  : isOwnerRow
                    ? _t("owner", "Owner")
                    : (p.email ?? "");

                return (
                  <div
                    key={participantId ?? p.email ?? i}
                    className={`applications-member${canNavigate ? " is-link" : ""}${isDeleted ? " is-deleted" : ""}${
                      // indented under the tenant they back (sorted right after them)
                      p.is_guarantor &&
                      participants.some(
                        (other) =>
                          toIdString(other?.id) &&
                          toIdString(other.id) === toIdString(p.guarantor_of)
                      )
                        ? " is-guarantorOf"
                        : ""
                    }`}
                    onClick={() => {
                      if (canNavigate) {
                        const url = new URL(window.location.href);
                        url.searchParams.set("tenant_id", participantId);
                        window.history.pushState({}, "", url.toString());
                        window.dispatchEvent(new PopStateEvent("popstate"));
                      }
                    }}
                  >
                    <Avatar
                      name={isDeleted ? "" : isMe ? "Yo" : displayName}
                      size="md"
                    />
                    <div className="applications-member__info">
                      <div className="applications-member__nameRow">
                        <p
                          className="applications-member__name"
                          title={displayName}
                        >
                          {displayName}
                        </p>
                        {isRoleSaving ? (
                          <Badge
                            tone="neutral"
                            size="xs"
                            className="applications-member__roleSaving"
                            aria-live="polite"
                          >
                            <span className="ds-spinner" aria-hidden="true" />
                            {_t("updating", "Updating…")}
                          </Badge>
                        ) : (
                          p.is_guarantor === true &&
                          !isDeleted && (
                            <Badge tone="neutral" size="xs">
                              {_t("guarantor", "Guarantor")}
                            </Badge>
                          )
                        )}
                      </div>
                      <p
                        className="applications-member__email"
                        title={subtitle}
                      >
                        {subtitle}
                      </p>
                    </div>
                    <div className="applications-member__status">
                      {isClientIdentificationConsentPending ? (
                        <ClientIdentificationPendingBadge
                          canSign={isMe && !llShowOnly}
                          onSign={() => onSignClientIdentification(app)}
                          _t={_t}
                        />
                      ) : (
                        <RoleBadge role={role} _t={_t} />
                      )}
                    </div>
                    {!llShowOnly &&
                      !isPendingInvitation &&
                      !isDeleted &&
                      p.email && (
                        <ParticipantRoleMenu
                          participant={p}
                          open={!!p.email && roleMenuEmail === p.email}
                          onToggle={() =>
                            setRoleMenuEmail(
                              roleMenuEmail === p.email ? null : p.email
                            )
                          }
                          onClose={() => setRoleMenuEmail(null)}
                          onChange={(nextRole) => openRoleConfirm(p, nextRole)}
                          busy={isRoleSaving}
                          guarantorBlocked={participants.some(
                            (other) =>
                              !other?.is_deleted &&
                              other?.is_guarantor === true &&
                              toIdString(p.id) &&
                              toIdString(other?.guarantor_of) ===
                                toIdString(p.id)
                          )}
                          _t={_t}
                        />
                      )}
                    {canNavigate && <Icon name="chevronRight" size={16} />}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── expanded: shared with (tenants only — hidden in landlord view) ── */}
          {!llShowOnly && totalShared > 0 && (
            <div className="applications-card__section applications-card__section--shared">
              <p className="applications-card__label">
                {_t("sharedWithSection", "SHARED WITH")}
              </p>

              {shareRequests.map((sr, i) => {
                const landlord = sr.landlord_id ?? {};
                const sharedBy = sr.tenant_id ?? {};
                const landlordName =
                  `${landlord.fname_companyName ?? ""} ${landlord.lname ?? ""}`.trim() ||
                  landlord.email ||
                  "";
                const landlordEmail = landlord.email ?? "";
                const sharedByName =
                  `${sharedBy.fname ?? ""} ${sharedBy.lname ?? ""}`.trim() ||
                  sharedBy.email ||
                  "";
                return (
                  <div key={sr._id ?? i} className="applications-member">
                    <Avatar name={landlordName || landlordEmail} size="md" />
                    <div className="applications-member__info">
                      <p className="applications-member__name">
                        {landlordName}
                      </p>
                      <p
                        className="applications-member__email"
                        title={landlordEmail}
                      >
                        {landlordEmail}
                      </p>
                    </div>
                    <div className="applications-member__status applications-member__status--stack">
                      {sharedByName && (
                        <span className="applications-member__email">
                          {_t("sharedBy", "Shared by")} {sharedByName}
                        </span>
                      )}
                      <Badge tone="warning">{_t("pending", "Pending")}</Badge>
                    </div>
                  </div>
                );
              })}

              {sharedProfiles.map((sp, i) => {
                const landlord = sp.id?.landlordid ?? {};
                const sharedBy = sp.shared_by ?? {};
                const landlordName =
                  `${landlord.fname_companyName ?? ""} ${landlord.lname ?? ""}`.trim() ||
                  landlord.email ||
                  "";
                const landlordEmail = landlord.email ?? "";
                const sharedByName = sp.shared_by_deleted
                  ? _t("deletedUser", "Deleted user")
                  : `${sharedBy.fname ?? ""} ${sharedBy.lname ?? ""}`.trim() ||
                    sharedBy.email ||
                    "";
                return (
                  <div key={sp.id?._id ?? i} className="applications-member">
                    <Avatar name={landlordName || landlordEmail} size="md" />
                    <div className="applications-member__info">
                      <p className="applications-member__name">
                        {landlordName}
                      </p>
                      <p
                        className="applications-member__email"
                        title={landlordEmail}
                      >
                        {landlordEmail}
                      </p>
                    </div>
                    {sharedByName && (
                      <div className="applications-member__status">
                        <span className="applications-member__email">
                          {_t("sharedBy", "Shared by")} {sharedByName}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      {roleConfirm && (
        // portaled, but React still bubbles clicks to the card: stop them
        <span onClick={(e) => e.stopPropagation()}>
          <Modal
            open
            size="sm"
            onClose={() => setRoleConfirm(null)}
            closeLabel={_t("close")}
            title={
              roleConfirm.role === "guarantor"
                ? _t("changeToGuarantorConfirmTitle", "Change to guarantor?")
                : _t("changeToFlatmateConfirmTitle", "Change to flatmate?")
            }
            description={
              roleConfirm.role === "guarantor"
                ? _t(
                    "changeToGuarantorConfirmText",
                    "{{name}} will be a guarantor in this application.",
                    { name: resolveParticipantName(roleConfirm.participant) }
                  )
                : _t(
                    "changeToFlatmateConfirmText",
                    "{{name}} will be a flatmate in this application.",
                    { name: resolveParticipantName(roleConfirm.participant) }
                  )
            }
            actions={
              <>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => setRoleConfirm(null)}
                >
                  {_t("cancel")}
                </Button>
                <Button
                  size="lg"
                  disabled={
                    roleConfirm.role === "guarantor" && !roleConfirm.guarantorOf
                  }
                  onClick={() => {
                    const { participant, role, guarantorOf } = roleConfirm;
                    setRoleConfirm(null);
                    handleRoleChange(participant, role, guarantorOf);
                  }}
                >
                  {_t("confirm")}
                </Button>
              </>
            }
          >
            {roleConfirm.role === "guarantor" &&
              (getGuarantorCandidates(roleConfirm.participant).length > 0 ? (
                <Select
                  id="role-confirm-guarantor-of"
                  label={_t("guarantorOf", "Guarantor of")}
                  value={roleConfirm.guarantorOf}
                  onChange={(e) =>
                    setRoleConfirm((current) => ({
                      ...current,
                      guarantorOf: e.target.value,
                    }))
                  }
                  options={getGuarantorCandidates(roleConfirm.participant).map(
                    (other) => ({
                      value: toIdString(other.id),
                      label: resolveParticipantName(other),
                    })
                  )}
                />
              ) : (
                <p className="applications-roleConfirm__empty">
                  {_t(
                    "noTenantToGuarantee",
                    "There is no tenant with an account in this application to guarantee yet."
                  )}
                </p>
              ))}
          </Modal>
        </span>
      )}
      {joinAs && (
        <span onClick={(e) => e.stopPropagation()}>
          <Modal
            open
            size="sm"
            onClose={() => setJoinAs(null)}
            closeLabel={_t("close")}
            title={_t("howDoYouWantToJoin", "How do you want to join?")}
            actions={
              <>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => setJoinAs(null)}
                >
                  {_t("cancel")}
                </Button>
                <Button
                  size="lg"
                  onClick={() => {
                    const choice = joinAs;
                    setJoinAs(null);
                    onAccept(app._id, choice);
                  }}
                >
                  {_t("continue")}
                </Button>
              </>
            }
          >
            <ChoiceGroup
              name={`join-as-${app._id}`}
              layout="list"
              value={joinAs}
              onChange={(event) => setJoinAs(event.target.value)}
              options={[
                {
                  value: "flatmate",
                  label: _t("joinAsFlatmate", "Join as flatmate"),
                  description: _t(
                    "joinAsFlatmateText",
                    "You will live in the apartment and share the application."
                  ),
                },
                {
                  value: "guarantor",
                  label: _t("joinAsGuarantor", "Join as guarantor"),
                  description: _t(
                    "joinAsGuarantorText",
                    "You will guarantee {{name}}'s rent and not live in the apartment.",
                    { name: resolveCreatorName(app.created_by) }
                  ),
                },
              ]}
            />
          </Modal>
        </span>
      )}
    </div>
  );
};

/* ── main modal ── */
const ApplicationsModal = ({
  isOpen,
  onClose,
  applications,
  loading,
  onNewApplication,
  onApplicationsChange,
  llShowOnly,
  pendingSkorProtectActions = [],
}) => {
  const { _t } = useTranslator();
  const { user, loadUser } = useContext(AuthContext);
  const currentUserEmail = user?.email?.toLowerCase();
  const currentUserId = user?._id;

  const {
    consentApplication,
    deleteApplication,
    createSkorProtectPaymentLink,
    loadPendingSkorProtectActions,
    getSkorProtectPaymentReceipt,
    reloadApplications,
  } = useContext(ApplicationContext);
  const [processingId, setProcessingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [clientIdentificationSigning, setClientIdentificationSigning] =
    useState(null);
  const [affidavitSigning, setAffidavitSigning] = useState(false);
  // Declaration to ask once the KYC-AML / household steps are closed
  const affidavitAfterStepsRef = useRef(false);
  const [paymentProcessingId, setPaymentProcessingId] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const resetPaymentProcessing = () => setPaymentProcessingId(null);

    window.addEventListener("pageshow", resetPaymentProcessing);
    return () => window.removeEventListener("pageshow", resetPaymentProcessing);
  }, []);

  useEffect(() => {
    if (!isOpen || pendingSkorProtectActions.length === 0) return undefined;

    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isOpen, pendingSkorProtectActions.length]);

  const handleAccept = async (applicationId, joinAs) => {
    const application = (applications ?? []).find(
      (app) => app?._id?.toString() === applicationId?.toString()
    );
    let accepted = false;
    try {
      setProcessingId(applicationId);
      await consentApplication(applicationId, "accepted", joinAs);
      accepted = true;
    } catch (err) {
      toast.error(err?.response?.data?.message ?? _t("failedToAcceptRequest"));
    } finally {
      setProcessingId(null);
    }
    // Already shared with a workspace that asks for the funds declaration:
    // ask for it right away (after the steps below, if they open)
    let affidavitNeeded = false;
    if (accepted && !haveValue(user?.consents?.declaration_consent)) {
      try {
        const pending = await getPendingSkorProtectActions();
        affidavitNeeded =
          String(pending?.success_res?.affidavit_action?.application_id) ===
          String(applicationId);
      } catch {
        // The Sign button stays on the card
      }
    }
    // Already shared with a landlord: open its steps (household and pets,
    // KYC-AML) right away. The Sign button stays for later.
    if (
      accepted &&
      application &&
      (application.client_identification_required === true ||
        (application.sharedProfiles ?? []).length > 0)
    ) {
      affidavitAfterStepsRef.current = affidavitNeeded;
      setClientIdentificationSigning({
        application,
        requireIdentification:
          application.client_identification_required === true,
      });
    } else if (affidavitNeeded) {
      setAffidavitSigning(true);
    }
  };

  const handleReject = async (applicationId) => {
    try {
      setProcessingId(applicationId);
      await consentApplication(applicationId, "rejected");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? _t("failedToRejectRequest"));
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (applicationId) => {
    try {
      setProcessingId(applicationId);
      await deleteApplication(applicationId);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? _t("failedToDeleteApplication")
      );
    } finally {
      setProcessingId(null);
    }
  };

  const handleSignClientIdentification = (application) => {
    setClientIdentificationSigning({ application });
  };

  if (clientIdentificationSigning) {
    return (
      <TenantRequirementsFlow
        user={user}
        mode="after"
        applicationId={clientIdentificationSigning.application._id}
        workspace={user?.workspaceId}
        requireIdentification={
          clientIdentificationSigning.requireIdentification ?? true
        }
        onIdentificationSigned={async () => {
          try {
            await reloadApplications();
            await loadUser?.("tenant");
          } catch (err) {
            toast.error(
              err?.response?.data?.message ?? _t("failedToAcceptRequest")
            );
          }
        }}
        onClose={() => {
          setClientIdentificationSigning(null);
          if (affidavitAfterStepsRef.current) {
            affidavitAfterStepsRef.current = false;
            setAffidavitSigning(true);
          }
        }}
      />
    );
  }

  if (affidavitSigning) {
    return (
      <ConsentStepModal
        isPopup
        user={user}
        selectedLandlord={{ affidavit_activated: true }}
        loadUserTenant={loadUser}
        onClose={async () => {
          setAffidavitSigning(false);
          await reloadApplications?.();
          await loadPendingSkorProtectActions?.();
        }}
      />
    );
  }

  const handlePaySkorProtect = async (pendingAction) => {
    try {
      setPaymentProcessingId(pendingAction.skor_protect_id);
      const paymentUrl = await createSkorProtectPaymentLink(
        pendingAction.skor_protect_id
      );
      if (!paymentUrl) throw new Error("Payment URL was not returned");
      window.location.assign(paymentUrl);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ??
          _t("failedToCreateSkorProtectPaymentLink")
      );
      loadPendingSkorProtectActions();
      setPaymentProcessingId(null);
    }
  };

  const handleViewPaymentReceipt = async (pendingAction) => {
    const receiptWindow = window.open("", "_blank");
    if (receiptWindow) receiptWindow.opener = null;

    try {
      const receiptUrl = await getSkorProtectPaymentReceipt(
        pendingAction.skor_protect_id
      );
      if (!receiptUrl) throw new Error("Receipt URL was not returned");

      if (receiptWindow) {
        receiptWindow.location.href = receiptUrl;
      } else {
        window.location.assign(receiptUrl);
      }
    } catch (err) {
      receiptWindow?.close();
      toast.error(
        err?.response?.data?.message ?? _t("failedToLoadPaymentReceipt")
      );
    }
  };

  if (!isOpen) return null;

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={_t("candidacies", "Candidacies")}
      description={
        llShowOnly
          ? _t(
              "applicationModelDescriptionLandlord",
              "The people applying together in this application."
            )
          : _t(
              "applicationModelDescription",
              "Introduce yourself to the landlord together with the people you will be living with."
            )
      }
      closeLabel={_t("close")}
      size="md"
      footer={
        !llShowOnly && (
          <Button
            size="lg"
            iconStart={<Icon name="plus" />}
            onClick={() => {
              onClose();
              onNewApplication();
            }}
          >
            {_t("newApplication")}
          </Button>
        )
      }
    >
      <div className="applicationsDrawer__list">
        {loading ? (
          <SkeletonList rows={3} variant="card" label={_t("loading")} />
        ) : (applications ?? []).length === 0 ? (
          <p className="applications-empty">
            {_t(
              "noApplicationsYet",
              "You don't have any applications yet. Create one and invite your flatmates."
            )}
          </p>
        ) : (
          (applications ?? []).map((app) => (
            <ApplicationCard
              key={app._id}
              app={app}
              currentUserEmail={currentUserEmail}
              currentUserId={currentUserId}
              currentUserClientIdentificationStatus={
                user?.consents?.client_identification_consent?.status
              }
              currentUserDeclarationConsent={
                user?.consents?.declaration_consent
              }
              onAccept={handleAccept}
              onReject={handleReject}
              onDelete={handleDelete}
              processingId={processingId}
              openMenuId={openMenuId}
              setOpenMenuId={setOpenMenuId}
              onCloseModal={onClose}
              llShowOnly={llShowOnly}
              onSignClientIdentification={handleSignClientIdentification}
              onSignAffidavit={() => setAffidavitSigning(true)}
              pendingSkorProtectAction={pendingSkorProtectActions.find(
                (action) => String(action.application_id) === String(app._id)
              )}
              now={now}
              onPaySkorProtect={handlePaySkorProtect}
              paymentProcessingId={paymentProcessingId}
              onViewPaymentReceipt={handleViewPaymentReceipt}
            />
          ))
        )}
      </div>
    </Drawer>
  );
};

export default ApplicationsModal;
